// Maps Stripe charges from useStripePayments into the Payment shape the rest
// of the dashboard already understands. Mirrors src/lib/ghlSync.js — keeps
// the calc layer (calculations.js) ignorant of which external system the
// money came from.
//
// Phase B (2026-05-17): Stripe becomes the source of truth for cash collected.
// Successful and partially-refunded charges are emitted as `status: "paid"`
// records that flow through state.payments alongside manual + GHL payments.

import { findClientByEmail, normEmail } from "./emailUtils";

// Convert a Stripe charge into a Payment-shaped object. Returns null if the
// charge doesn't represent collected cash (failed / pending / fully refunded).
//
// amount = grossAmount - refundedAmount so partial refunds reduce the "paid"
// total naturally. Fully refunded charges return null (no net cash).
export function mapStripeChargeToPayment(charge, clients) {
  if (!charge) return null;
  if (charge.status !== "succeeded" && charge.status !== "partially_refunded") {
    return null;
  }
  const net = (charge.grossAmount || 0) - (charge.refundedAmount || 0);
  if (net <= 0) return null;

  // Resolve client: manual override wins, else email-based auto-match.
  let clientId = "";
  if (charge.clientIdOverride) {
    const byId = clients.find((c) => c.id === charge.clientIdOverride);
    if (byId) clientId = byId.id;
  }
  if (!clientId) {
    const matched = findClientByEmail(charge.customerEmail, clients);
    if (matched) clientId = matched.id;
  }

  return {
    id: `stripe:${charge.id}`,
    _source: "stripe",
    _readonly: true,
    _stripeChargeId: charge.id,
    _stripeCustomerEmail: charge.customerEmail || "",
    _stripeCustomerName: charge.customerName || "",
    clientId,
    offerId: charge.offerId || "",
    amount: net,
    // Stripe processor fee on this charge — propagated through state.payments
    // so the calc engine can subtract it from gross revenue when building
    // Net Revenue / Cash in Bank. Defaults to 0 if Stripe didn't report it.
    stripeFee: Number(charge.fee) || 0,
    bookedDate: charge.date || null,
    dueDate: charge.date || null,
    paidDate: charge.date || null,
    status: "paid",
    note: charge.description || "",
  };
}

// Dedup GHL-or-manual payments against Stripe payments. Stripe wins because
// it's the actual cash event; the GHL invoice is just the "expected"
// counterpart, and a manual entry is what Ahmed typed in before Stripe was
// connected.
//
// A Stripe payment dedups a non-Stripe payment when ALL of:
//   1. Same normalized customer email (or same clientId if both have one)
//   2. Amount within $1
//   3. Paid date within 7 days
//
// Both criteria-bands are loose enough to forgive Stripe fees + processing
// lag, tight enough to avoid collapsing two distinct payments.
export function mergeStripeWithOthers(otherPayments, stripePayments) {
  if (!stripePayments || stripePayments.length === 0) return otherPayments;
  const used = new Set();
  const survivors = otherPayments.filter((p) => {
    // Only consider deduping "paid" non-Stripe rows; non-paid rows (pending,
    // scheduled) can't possibly be the same event.
    if (p.status !== "paid" || p._source === "stripe") return true;

    const pEmail = normEmail(p._ghlCustomerEmail || p.customerEmail || "");
    const pDate = p.paidDate ? new Date(p.paidDate).getTime() : null;

    for (const s of stripePayments) {
      if (used.has(s.id)) continue;
      const sEmail = normEmail(s._stripeCustomerEmail || "");
      const matchByEmail = pEmail && sEmail && pEmail === sEmail;
      const matchByClient = p.clientId && s.clientId && p.clientId === s.clientId;
      if (!matchByEmail && !matchByClient) continue;

      const amountDelta = Math.abs((p.amount || 0) - (s.amount || 0));
      if (amountDelta > 1) continue;

      if (pDate && s.paidDate) {
        const sDate = new Date(s.paidDate).getTime();
        const daysApart = Math.abs(pDate - sDate) / (1000 * 60 * 60 * 24);
        if (daysApart > 7) continue;
      }

      used.add(s.id);
      return false; // Stripe wins — drop this non-Stripe payment
    }
    return true;
  });
  return [...survivors, ...stripePayments];
}
